// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, Spinner, TextInput, useToast } from '@loa/ui-components';
import { TiptapEditor } from '../../invitation/TiptapEditor';
import { proposalApi } from '../../../api/proposalApi';
import { intelligenceApi, sendMessageAwaitingReply } from '../../../api/intelligenceApi';
import { SOS_AI_SECTION_KEYS, type SuitabilityStatement } from '../../../types/proposal';

export type WizardStepSosBuilderProps = Readonly<{
  proposalId: string;
  /** Links the drafting session to the relationship, when known. */
  profileId?: string;
  /** Returns the wizard to Step 2 (quote options). */
  onBack: () => void;
  /** Fires once the branded PDF has been generated — advances to Step 4. */
  onGenerated: (statement: SuitabilityStatement) => void;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

/**
 * Best-effort extraction of the empty section key from the backend's
 * `SOS_SECTION_EMPTY: section 'xyz' is empty` message shape, so the builder
 * can jump the advisor straight to the offending section.
 */
function extractEmptySectionKey(message: string | undefined): string | null {
  if (!message) return null;
  const match = /section '([^']+)' is empty/.exec(message);
  return match ? match[1] : null;
}

/**
 * Step 3 of the pension proposal wizard (Figma 1453-62555 … 63108): the
 * Statement of Suitability builder. A two-pane editor — a left rail of the
 * six canonical sections, and a right rich-text editor (the same Tiptap
 * editor the Notes feature uses) bound to whichever section is active.
 *
 * `needs_objectives` and `suitability_recommendations` additionally offer a
 * "Draft with AI" affordance backed by the intelligence-service SOS_BUILDER
 * use case — the other four sections are template-seeded merge-field /
 * boilerplate content with no AI button (PRD §3.1 Step 3 table).
 */
export function WizardStepSosBuilder({
  proposalId,
  profileId,
  onBack,
  onGenerated,
}: WizardStepSosBuilderProps) {
  const { showToast } = useToast();

  const [statement, setStatement] = useState<SuitabilityStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiDrafting, setAiDrafting] = useState<Record<string, boolean>>({});
  const [aiInstructions, setAiInstructions] = useState<Record<string, string>>({});
  const [aiUnavailable, setAiUnavailable] = useState<Record<string, boolean>>({});
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    proposalApi
      .getSuitabilityStatement(proposalId)
      .then((s) => {
        if (cancelled) return;
        setStatement(s);
        const initialDrafts: Record<string, string> = {};
        s.sections.forEach((sec) => {
          initialDrafts[sec.sectionKey] = sec.html;
        });
        setDrafts(initialDrafts);
        setDirtyKeys(new Set());
        setActiveKey((prev) => prev ?? s.sections[0]?.sectionKey ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(apiErrorMessage(err, 'Failed to load the Statement of Suitability'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  const activeSection = useMemo(
    () => statement?.sections.find((s) => s.sectionKey === activeKey) ?? null,
    [statement, activeKey],
  );

  const handleContentChange = useCallback((key: string, html: string) => {
    setDrafts((prev) => ({ ...prev, [key]: html }));
    setDirtyKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const buildDirtyPayload = useCallback((): Record<string, string> => {
    const payload: Record<string, string> = {};
    dirtyKeys.forEach((key) => {
      if (drafts[key] !== undefined) payload[key] = drafts[key];
    });
    return payload;
  }, [dirtyKeys, drafts]);

  const persistDirtySections = useCallback(async (): Promise<SuitabilityStatement | null> => {
    const payload = buildDirtyPayload();
    if (Object.keys(payload).length === 0) return statement;
    const updated = await proposalApi.updateSuitabilityStatement(proposalId, payload);
    setStatement(updated);
    setDirtyKeys(new Set());
    return updated;
  }, [buildDirtyPayload, proposalId, statement]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      await persistDirtySections();
      showToast('Statement of Suitability saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save the Statement of Suitability'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, persistDirtySections, showToast]);

  const handleGenerate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    try {
      await persistDirtySections();
      const generated = await proposalApi.generateSuitabilityDocument(proposalId);
      setStatement(generated);
      onGenerated(generated);
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to generate the Statement of Suitability');
      const emptySectionKey = extractEmptySectionKey(message);
      if (emptySectionKey) {
        setActiveKey(emptySectionKey);
        const title =
          statement?.sections.find((s) => s.sectionKey === emptySectionKey)?.title ?? emptySectionKey;
        showToast(`"${title}" is empty — add content before generating.`, 'error');
      } else {
        showToast(message, 'error');
      }
    } finally {
      setGenerating(false);
    }
  }, [generating, persistDirtySections, proposalId, onGenerated, showToast, statement]);

  const ensureChatSession = useCallback(async (): Promise<string | null> => {
    if (chatSessionId) return chatSessionId;
    try {
      const session = await intelligenceApi.createSession('SOS_BUILDER', profileId);
      setChatSessionId(session.sessionId);
      return session.sessionId;
    } catch {
      return null;
    }
  }, [chatSessionId, profileId]);

  const handleDraftWithAi = useCallback(
    async (sectionKey: string) => {
      setAiUnavailable((prev) => ({ ...prev, [sectionKey]: false }));
      setAiDrafting((prev) => ({ ...prev, [sectionKey]: true }));
      try {
        const sessionId = await ensureChatSession();
        if (!sessionId) throw new Error('AI session unavailable');
        const instruction = aiInstructions[sectionKey]?.trim();
        const message = `Draft the ${sectionKey} section for proposal ${proposalId}.${
          instruction ? ` Instruction: ${instruction}` : ''
        }`;
        const reply = await sendMessageAwaitingReply(sessionId, message);
        const updated = await proposalApi.applyAiSection(proposalId, sectionKey, reply, sessionId);
        setStatement(updated);
        const section = updated.sections.find((s) => s.sectionKey === sectionKey);
        if (section) {
          setDrafts((prev) => ({ ...prev, [sectionKey]: section.html }));
        }
        setDirtyKeys((prev) => {
          const next = new Set(prev);
          next.delete(sectionKey);
          return next;
        });
        showToast('AI draft applied', 'success');
      } catch {
        // AI assistance is an accelerator, never a dependency — manual
        // editing always remains available.
        setAiUnavailable((prev) => ({ ...prev, [sectionKey]: true }));
      } finally {
        setAiDrafting((prev) => ({ ...prev, [sectionKey]: false }));
      }
    },
    [ensureChatSession, aiInstructions, proposalId, showToast],
  );

  if (loading) {
    return (
      <div
        className="pension-wizard-step"
        style={{ display: 'flex', justifyContent: 'center' }}
        data-testid="sos-builder-loading"
      >
        <Spinner />
      </div>
    );
  }

  if (loadError || !statement) {
    return (
      <div className="pension-wizard-step" data-testid="sos-builder-error">
        {loadError ?? 'Failed to load the Statement of Suitability'}
      </div>
    );
  }

  return (
    <div className="pension-wizard-step" data-testid="wizard-step-sos-builder">
      <h2 className="pension-wizard-step__title">Statement of Suitability</h2>

      {statement.status === 'GENERATED' && (
        <div className="sos-builder__banner" data-testid="sos-builder-generated-banner">
          Document generated — editing will require regeneration.
        </div>
      )}

      <div className="sos-builder">
        <nav className="sos-builder__rail" aria-label="Statement of Suitability sections">
          {statement.sections.map((section) => (
            <button
              key={section.sectionKey}
              type="button"
              className={
                section.sectionKey === activeKey
                  ? 'sos-builder__rail-item sos-builder__rail-item--active'
                  : 'sos-builder__rail-item'
              }
              onClick={() => setActiveKey(section.sectionKey)}
              data-testid={`sos-builder-rail-item-${section.sectionKey}`}
            >
              {section.title}
              {dirtyKeys.has(section.sectionKey) && (
                <span aria-hidden="true" className="sos-builder__rail-item-dot" />
              )}
            </button>
          ))}
        </nav>

        <div className="sos-builder__content">
          {activeSection && (
            <>
              {SOS_AI_SECTION_KEYS.has(activeSection.sectionKey) && (
                <div className="sos-builder__ai-row" data-testid="sos-builder-ai-row">
                  <TextInput
                    placeholder="Optional steering instruction for the AI draft…"
                    value={aiInstructions[activeSection.sectionKey] ?? ''}
                    onChange={(e) =>
                      setAiInstructions((prev) => ({
                        ...prev,
                        [activeSection.sectionKey]: e.target.value,
                      }))
                    }
                    disabled={aiDrafting[activeSection.sectionKey]}
                    fluid
                    data-testid={`sos-builder-ai-instruction-${activeSection.sectionKey}`}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => handleDraftWithAi(activeSection.sectionKey)}
                    disabled={Boolean(aiDrafting[activeSection.sectionKey])}
                    loading={Boolean(aiDrafting[activeSection.sectionKey])}
                    data-testid={`sos-builder-ai-draft-btn-${activeSection.sectionKey}`}
                  >
                    Draft with AI
                  </Button>
                </div>
              )}
              {aiUnavailable[activeSection.sectionKey] && (
                <div
                  className="sos-builder__ai-unavailable"
                  data-testid={`sos-builder-ai-unavailable-${activeSection.sectionKey}`}
                >
                  AI drafting unavailable — edit manually.
                </div>
              )}

              <TiptapEditor
                key={activeSection.sectionKey}
                content={drafts[activeSection.sectionKey] ?? ''}
                onChange={(html) => handleContentChange(activeSection.sectionKey, html)}
                minHeight="360px"
                maxHeight="600px"
                ariaLabel={`${activeSection.title} content`}
                disabled={Boolean(aiDrafting[activeSection.sectionKey])}
              />
            </>
          )}
        </div>
      </div>

      <div className="pension-wizard-step__footer">
        <Button variant="outlined" onClick={onBack} disabled={saving || generating}>
          Back to quote options
        </Button>
        <Button
          variant="outlined"
          onClick={handleSave}
          disabled={saving || generating}
          loading={saving}
          data-testid="sos-builder-save-btn"
        >
          Save
        </Button>
        <Button
          variant="filled"
          onClick={handleGenerate}
          disabled={saving || generating}
          loading={generating}
          data-testid="sos-builder-generate-btn"
        >
          Generate Statement Of Suitability
        </Button>
      </div>
    </div>
  );
}

export default WizardStepSosBuilder;
